import React, { useState, useEffect } from 'react';
import {getPlatform, pageBack} from "@/hooks";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import LoadingSpinner from "@/mobile/common/LoadingSpinner";
import BirthdatePicker from "@/common/customer.additional-data-fields/BirthdatePicker";
import PostcodePicker from "@/common/customer.additional-data-fields/PostcodePicker";
import SaveButton from "@/common/customer.additional-data-fields/SaveButton";
import DataCandy from "@/common/customer.additional-data-fields/DataCandy";
import Spoonity from './Spoonity';

const {
    Customer,
    headoffice: {
        customer_data_fields_to_capture,
    }
} = Mobi2Go;

const baseClass = (class_name = '') => `m2g-customer-details${class_name}`;

const formatDate = (str) => {
    if (str) {
        const arr = str.split(',');
        return arr.join('/');
    } return '';
}

const AdditionalDataFieldsToCapture = ({ closeModal }) => {
    const [loyalty, setLoyalty] = useState(null);
    const [birthdate, setBirthdate] = useState('');
    const [postcode, setPostcode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isOptedInToLoyalty, setIsOptedInToLoyalty] = useState(false);

    const [t] = useTranslation('customer.details');

    const updateCustomerState = () => {
        setLoyalty(Customer.getLoyalty());
        setBirthdate(formatDate(Customer.getDateOfBirth()?.format('YYYY-MM-DD') ?? ''));
        setPostcode(Customer.getPostCode() ?? '');
        setIsOptedInToLoyalty(Customer.getLoyaltyOptIn());
    }

    const updateCustomerDetails = () => {
        setBirthdate(formatDate(Customer.getDateOfBirth()?.format('YYYY-MM-DD') ?? ''));
        setPostcode(Customer.getPostCode() ?? '');
    }

    useEffect(() => {
        updateCustomerState();

        Customer.bind({
            logIn: updateCustomerState,
            logOut: updateCustomerState,
            update: updateCustomerDetails,
            updateLoyalty: updateCustomerState
        });

        return () => {
            Customer.unbind({
                logIn: updateCustomerState,
                logOut: updateCustomerState,
                update: updateCustomerDetails,
                updateLoyalty: updateCustomerState
            });
        }
    }, []);


    if (loyalty && 'DataCandyLoyalty' in loyalty) {
        // DataCandy has their own custom setup for collecting additional data fields and does not use the customer_data_fields_to_capture setting
        return <DataCandy
            closeModal={closeModal}
            baseClass={baseClass}
            formatDate={formatDate}
            t={t}
            loyalty={loyalty.DataCandyLoyalty}
            birthdate={birthdate}
            postcode={postcode}
            setBirthdate={setBirthdate}
            setPostcode={setPostcode}
        />;
    }

    if (
        loyalty?.SpoonityLoyalty?.program?.settings?.authentication_enabled &&
        !isOptedInToLoyalty
    ) {
        return <Spoonity
            closeModal={closeModal}
            baseClass={baseClass}
        />
    }

    const handleSave = (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            Customer.update({
                date_of_birth: birthdate,
                post_code: postcode
            }, (status, error) => {
                if (status === 'error' && error) {
                    throw error;
                } else {
                    updateCustomerState();
                    setIsLoading(false);
                    if (getPlatform() === 'web') {
                        closeModal();
                    } else {
                        pageBack();
                    }
                }
            })

        } catch (error) {
            updateCustomerState();
            setIsLoading(false);
            throw error;
        }
    }

    return (
        <>
            {customer_data_fields_to_capture.birthdate &&
                <BirthdatePicker
                    handleChange={setBirthdate}
                    baseClass={baseClass}
                    birthdate={birthdate}
                />
            }

            {customer_data_fields_to_capture.postcode &&
                <PostcodePicker
                    handleChange={setPostcode}
                    baseClass={baseClass}
                    postcode={postcode}
                    t={t}
                />
            }

            {isLoading &&
                <LoadingSpinner
                    className={baseClass('__spinner')}
                />
            }

            {Object.values(customer_data_fields_to_capture).includes(true) &&
                <SaveButton
                    handleSave={handleSave}
                    baseClass={baseClass}
                    saveButtonDisabled={isLoading}
                    text={t('save')}
                />
            }
        </>
    );
}

AdditionalDataFieldsToCapture.defaultProps = {
    closeModal: () => null
}

AdditionalDataFieldsToCapture.propTypes = {
    closeModal: PropTypes.func
}
export default AdditionalDataFieldsToCapture;
